import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { CHANGE, RELINK, RECONNECT_LABEL } from 'src/payments/constants';
import { tracker } from 'src/utils/analytics';
import { useIntersectionObserver } from 'src/hooks/use-intersection-observer';

type BankActionsProps = {
  bankNeedsRelinking: boolean;
  relinkBankAccount: () => void;
  editBankAccount: () => void;
};

type RelinkButtonComponentProps = {
  onClick: () => void;
};

const RelinkButtonComponent = ({ onClick }: RelinkButtonComponentProps): JSX.Element => {
  const ref = useRef<HTMLButtonElement>(null);
  const entry = useIntersectionObserver(ref, { threshold: 0.1, freezeOnceVisible: true });

  useEffect(() => {
    if (entry?.isIntersecting) {
      tracker.dutchiePayRelinkButtonViewed({});
    }
  }, [entry?.isIntersecting]);

  const handleClick = (): void => {
    tracker.dutchiePayRelinkButtonClicked({});
    onClick();
  };

  return (
    <RelinkButton ref={ref} aria-live='polite' aria-label={RECONNECT_LABEL} onClick={handleClick}>
      {RELINK}
    </RelinkButton>
  );
};

export const BankActions = ({
  bankNeedsRelinking,
  relinkBankAccount,
  editBankAccount,
}: BankActionsProps): JSX.Element => (
  <Actions>
    <ChangeButton data-testid='change-bank-account' onClick={editBankAccount}>
      {CHANGE}
    </ChangeButton>
    {bankNeedsRelinking && <RelinkButtonComponent onClick={relinkBankAccount} />}
  </Actions>
);

export const Actions = styled.div`
  ${({ theme: { spaces } }) => css`
    display: flex;
    align-items: center;
    gap: ${spaces[2]};
  `}
`;

export const RelinkButton = styled.button`
  ${({ theme: { spaces, colors } }) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    background: ${colors.white};
    padding: ${spaces[2]} ${spaces[3]};
    gap: ${spaces[2]};
    border-radius: 4px;
    border: 1px solid #be0517;
    color: #be0517;
    text-align: center;
    font: normal 600 12px/16px 'Matter';
    letter-spacing: 0.06px;
    height: ${spaces[8]};
    &:hover {
      background: #be0517;
      color: white;
    }
  `}
`;

export const ChangeButton = styled.button`
  color: ${({ theme: { colors } }) => colors.grey[45]};
  font: 400 13px 'Proxima Nova', sans-serif;
  text-decoration: underline;
  background: none;
  border: none;
  cursor: pointer;
`;
